import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import {
	BusyIndicator,
	Dialog,
	NoResults,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useOpenDialog,
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone,
	useWarnBeforeUnload
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CommunityPostDialog.module.less";
import { useCommunityPostContent } from "../HubCommentSection/hooks/useCommunityPostContent";
import { PostViewer } from "./PostViewer";
import { never } from "client_legacy/ndaw/errors";
import { getHubUrlForCommunity } from "./CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { CommunityPostDialogCommentSection } from "./CommunityPostDialogCommentsSection";
import { TimeZone } from "client/DateAndTime/timezones";
import classNames from "classnames";
import { CommentThread } from "../HubCommentSection/CommentThread";
import { getRelatedCommentThread } from "client/Api/Communities/communityPostViewer";
import { getCommunityCommentService } from "./getCommunityService";
import { deleteCommunityPost } from "client/Api/Communities/communityPosts";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HubCommentSectionService } from "../HubCommentSection/HubCommentSection";
import { HubCommentModel } from "client/Api/Hub/common";
import { MobileCommentBoxPreservedSpace } from "../HubCommentSection/MobileComment/MobileCommentBoxPreservedSpace";

export type CommunityPostDialogDeepLink = {
	kind: "comment";
	commentId: number;
} | {
	kind: "comments";
};

export type CommunityPostDialogProps = {
	id: number;
	onClose: () => void;
	deepLink?: CommunityPostDialogDeepLink;
};

export const CommunityPostDialog = (props: CommunityPostDialogProps): ReactElement => {
	const {
		onClose: onCloseFromProps,
		id,
		deepLink
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isEditingMobileComment, setIsEditingMobileComment] = useState<boolean>(false);
	const [commentsState, setCommentsState] = useState<CommentsState>(
		(): CommentsState => {
			return deepLink?.kind === "comment"
				? {
					kind: "comment-thread",
					commentId: deepLink.commentId
				}
				: {
					kind: "all-comments",
					shouldReserveSpace: false
				};
		}
	);

	const commentsContainer: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const userTimezone: TimeZone = useUserTimezone();
	const deepLinkRef: ReadonlyRefObject<typeof deepLink> = useSynchronizedRef(deepLink);
	useWarnBeforeUnload(isEditingMobileComment);

	const onClose = useCallback(
		async (): Promise<void> => {
			if (isEditingMobileComment) {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToLeaveThisPage"),
					children: localize("_YouHaveStartedWritingOrEditingAComment"),
					confirmText: localize("_Stay"),
					cancelText: localize("_Leave"),
					trackingId: "discard-community-comment-form-confirmation-dialog"
				});

				if (result.wasSuccessful) {
					return;
				}
			}

			onCloseFromProps();
		},
		[
			isEditingMobileComment,
			onCloseFromProps,
			openConfirmationDialog
		]
	);

	const firstFetchDoneHandler = useCallback(
		(): void => {
			const { current: deepLink } = deepLinkRef;

			if (!deepLink) {
				return;
			}

			switch (deepLink.kind) {
				case "comments":
					setCommentsState({
						kind: "all-comments",
						shouldReserveSpace: true
					});

					// We need timeout to invoke the
					// the scrolling after rendering of
					// the DOM of the page
					setTimeout(
						(): void => {
							commentsContainer.current
								?.scrollIntoView({
									behavior: "smooth",
									block: "start"
								});
						},
						1
					);
					break;
				case "comment":
					// Nothing to do
					break;
				default:
					never(deepLink);
			}
		},
		[deepLinkRef]
	);

	const {
		reloadPostHandler,
		isFollowingChangedHandler,
		postState,
		retrievePostStatistics,
		statisticsRetrievedHandler
	} = useCommunityPostContent(
		id,
		firstFetchDoneHandler
	);

	const navigateToCommunityHandler = useCallback(
		(): void => {
			if (postState.loadingStatus !== "loaded") {
				return;
			}

			const communityUrl: string = getHubUrlForCommunity({
				source: HubDataSource.Community,
				communityId: postState.post
					.community
					.id
			});

			navigateTo(communityUrl);

		},
		[postState]
	);

	const allCommentViewHandler = useCallback(
		(): void => {
			setCommentsState({
				kind: "all-comments",
				shouldReserveSpace: true
			});

			setTimeout(
				(): void => {
					commentsContainer.current
						?.scrollIntoView({
							behavior: "smooth",
							block: "start"
						});
				},
				0
			);
		},
		[]
	);

	const deletePostHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "delete-post-confirmation-dialog",
				title: localize("_AreYouSureYouWantDoDeleteThisPost"),
				confirmText: localize("_Delete"),
				cancelText: localize("_Cancel")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteCommunityPost(id, globalCancellation);
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			id,
			onClose,
			openConfirmationDialog
		]
	);

	const communityCommentService = useMemo(
		(): HubCommentSectionService => {
			return getCommunityCommentService(id);
		},
		[id]
	);

	const getRelatedCommentThreadHandler = useCallback(
		(commentId: number, signal?: AbortSignal): Promise<readonly HubCommentModel[]> => {
			return getRelatedCommentThread(
				id,
				commentId,
				signal
			);
		},
		[id]
	);

	const renderContent = (): ReactElement => {
		switch (postState.loadingStatus) {
			case "loading":
				return (
					<BusyIndicator />
				);
			case "error":
				return (
					<NoResults
						trackingId="loading-error"
						icon="gear"
						title={localize("_ItemNotFound")}
						message={localize("_WeWereUnableToLoadTheItem")}
						onRetry={reloadPostHandler}
						height="fill"
					/>
				);
			case "not-found":
				return (
					<NoResults
						trackingId="item-not-found"
						icon="users"
						title={localize("_ItemNotFound")}
						message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
						height="fill"
					/>
				);
			case "loaded":
				const { post, options } = postState;
				const commentsCount: number = post.statistics
					.commentsCount;

				return (
					<div className={styles.content}>
						<PostViewer
							post={post}
							onAfterUpdate={reloadPostHandler}
							onIsFollowingChanged={isFollowingChangedHandler}
							onDelete={deletePostHandler}
							postOptions={options}
							onStatisticsRetrieved={statisticsRetrievedHandler}
							onNavigateToCommunity={navigateToCommunityHandler}
						/>
						<div ref={commentsContainer}>
							{commentsState.kind === "comment-thread"
								? (
									<CommentThread
										commentId={commentsState.commentId}
										onStatisticsUpdate={retrievePostStatistics}
										onMobileCommentIsEditingChanged={setIsEditingMobileComment}
										options={options}
										timezone={userTimezone}
										onAllCommentView={allCommentViewHandler}
										allCommentsCount={commentsCount}
										getRelatedCommentThread={getRelatedCommentThreadHandler}
										service={communityCommentService}
									/>
								)
								: (
									<div
										className={
											classNames({
												[styles.allCommentsContainer_withMinHeight]: commentsState.shouldReserveSpace
											})
										}
									>
										<CommunityPostDialogCommentSection
											onPostStatisticsUpdate={retrievePostStatistics}
											onMobileCommentIsEditingChanged={setIsEditingMobileComment}
											options={options}
											timezone={userTimezone}
											commentsCount={commentsCount}
											service={communityCommentService}
										/>
									</div>
								)
							}
							<MobileCommentBoxPreservedSpace />
						</div>
					</div>
				);
			default:
				never(postState);
		}
	};

	return (
		<>
			<Dialog
				trackingId="community-post-dialog"
				variant="left-slider"
				onClose={onClose}
				hasBusyIndicator={isBusy}
			>
				<Dialog.Header
					hasBorder
					title={localize("_CommunityPost")}
					onClose={onClose}
				/>
				<Dialog.Body
					goesEdgeToEdge
				>
					<div className={styles.container}>
						{renderContent()}
					</div>
				</Dialog.Body>
				<Dialog.Footer />
			</Dialog>
			{confirmationDialog}
		</>
	);
};
CommunityPostDialog.displayName="CommunityPostDialog";

export type HubDetailsDialogOpenData = OmitDistributive<CommunityPostDialogProps, "onClose">;

type CommentsState = {
	kind: "all-comments";
	shouldReserveSpace: boolean;
} | {
	kind: "comment-thread";
	commentId: number;
};
export function useCommunityPostDialog(): [
	element: ReactElement | null,
	open: (props: HubDetailsDialogOpenData) => Promise<void>
] {
	return useOpenDialog<HubDetailsDialogOpenData>(
		(props, onClose) => (
			<CommunityPostDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}